import React from 'react'
// @src imports
import {
  selectedColor,
  selectedFontId,
  selectedFontSize,
  selectedTextAlign,
} from 'src/redux/selectors/text'
import SignatureList from 'src/editor/components/SignatureList/SignatureList'
import FontList from 'src/editor/components/FontList/FontList'
import { Icons } from 'src/chrome'
import { useState } from 'src/hooks'
import { defaultTextSelection } from 'src/editor/text/types'
import { defaultFragment } from 'src/editor/text/ElementText'
import { textEditor } from 'src/helpers'
// relative imports
import ColorPicker from '../ColorPicker/ColorPicker'
import FontSizeSlider from '../FontSizeSlider/FontSizeSlider'
import InsertNames from '../InsertNames/InsertNames'
import TextAlignmentComponent from '../TextAlignment/TextAlignment'
import TextBarMobile from '../TextBarMobile/TextBarMobile'
import { Api } from '../../api'
import { Steps } from '../../types'

import styles from './minimalTextOptions.module.scss'

type ControlName =
  | 'Alignment'
  | 'Size'
  | 'Typography'
  | 'Color'
  | 'Insert Name'
  | 'Insert Signature'

const items: {
  name: ControlName
  icon: Icons
  viewBox?: string
  size: number
}[] = [
  {
    name: 'Alignment',
    icon: 'ALIGNLEFT',
    viewBox: '0 0 46 32',
    size: 18,
  },
  {
    name: 'Size',
    icon: 'RULER',
    viewBox: '0 0 32 32',
    size: 20,
  },
  {
    name: 'Typography',
    icon: 'FONT2',
    size: 18,
  },
  {
    name: 'Color',
    icon: 'COLOR',
    viewBox: '0 0 32 32',
    size: 20,
  },
  {
    name: 'Insert Name',
    icon: 'ADDUSER',
    size: 18,
  },
  {
    name: 'Insert Signature',
    icon: 'SIGNATURE',
    viewBox: '0 0 36 32',
    size: 22,
  },
]

type Props = {
  api: Api & { step: { type: Steps.EditText } }
}

const MinimalTextOptions: React.FC<Props> = props => {
  const { api } = props

  const selectedElement = api.getSelectedElement(api.step)
  const textState = {
    selection: api.step.textSelection ?? defaultTextSelection,
    text: selectedElement.text ?? [defaultFragment],
  }
  const textAlign = selectedTextAlign(textState)
  const fontSize = selectedFontSize(textState)
  const fontId = selectedFontId(textState)
  const color = selectedColor(textState)
  const utils = textEditor(api.updateText, api.step)

  const [activeControl, setActiveControl] = useState<ControlName>('Alignment')

  return (
    <div
      className={
        activeControl !== 'Typography' ? styles.minimalTextBar : styles.style
      }
    >
      <TextBarMobile
        tool={activeControl}
        onClick={activeControl =>
          setActiveControl(activeControl as ControlName)
        }
        items={items}
      />
      <div
        className={
          activeControl === 'Typography'
            ? `${styles.controlContainer} ${styles.fullView}`
            : activeControl === 'Color'
            ? `${styles.controlContainer} ${styles.colorPicker}`
            : styles.controlContainer
        }
      >
        {activeControl === 'Alignment' && (
          <TextAlignmentComponent
            textAlign={textAlign}
            selectTextAlign={utils.updateTextAlign}
          />
        )}
        {activeControl === 'Size' && (
          <FontSizeSlider
            fontSize={fontSize}
            selectFontSize={fontSize => utils.updateProperties({ fontSize })}
          />
        )}
        {activeControl === 'Typography' && (
          <FontList
            fonts={api.fonts}
            fontId={fontId}
            selectFont={fontId => utils.updateProperties({ fontId })}
          />
        )}
        {activeControl === 'Color' && (
          <ColorPicker
            className={styles.flexRow}
            selectedColor={color}
            selectColor={color => utils.updateProperties({ color })}
          />
        )}
        {activeControl === 'Insert Name' && (
          <InsertNames insertPlaceholder={utils.insertPlaceholder} />
        )}
        {activeControl === 'Insert Signature' && (
          <SignatureList
            signatures={api.fonts.signatures}
            className={styles.fontList}
            onClick={utils.insertSignature}
          />
        )}
      </div>
    </div>
  )
}

export default MinimalTextOptions
